import { getToken, getRefreshToken, setSession, clearSession, getTokenExpiry } from "./auth";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "http://localhost:5020";

// Send a client-side error to the backend for Serilog to record.
// Never throws — error reporting must not cause more errors.
export function reportClientError(payload) {
  try {
    const token = getToken();
    fetch(`${API_BASE_URL}/api/client-errors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        ...payload,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => {});
  } catch {
    // Swallow — never let the reporter crash the page
  }
}

// Attempt a silent token refresh. Returns true if successful, false otherwise.
// All concurrent callers share the same in-flight promise so the refresh
// endpoint is called exactly once regardless of how many 401s arrive together.
let _refreshPromise = null;
async function tryRefresh() {
  if (_refreshPromise) return _refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setSession({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return true;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

// Proactively refresh the access token ~2 minutes before it expires.
// Returns a cancel function — call it to stop the scheduler (e.g. on logout).
// Handles multi-tab scenarios by listening to the storage event: when another
// tab refreshes and writes a new token, the timer resets to the new expiry.
export function scheduleTokenRefresh() {
  let timer = null;
  let cancelled = false;

  function schedule() {
    if (cancelled) return;
    clearTimeout(timer);

    const expiry = getTokenExpiry();
    if (!expiry || !getToken()) return;

    const delay = Math.max(0, expiry - Date.now() - 2 * 60 * 1000);

    timer = setTimeout(async () => {
      if (cancelled) return;
      const ok = await tryRefresh();
      if (ok) {
        schedule(); // reschedule with the new token's expiry
      } else {
        clearSession();
        window.location.href = "/login";
      }
    }, delay);
  }

  function handleStorage() {
    // Another tab updated localStorage (new tokens written after its refresh).
    // Reset our timer so we don't race against it.
    if (getToken()) schedule();
  }

  schedule();
  window.addEventListener("storage", handleStorage);

  return () => {
    cancelled = true;
    clearTimeout(timer);
    window.removeEventListener("storage", handleStorage);
  };
}

export async function apiFetch(path, options = {}) {
  const token = getToken();

  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const isFormData = options.body instanceof FormData;

  const res = await fetch(url, {
    method: options.method || "GET",
    cache: "no-store", // prevent 304 stale cache responses after API contract changes
    headers: {
      ...(!isFormData && { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(options.headers || {}),
    },
    body: options.body,
  });

  // Only attempt refresh when a request was made with an existing token.
  // On the login page there is no token, so a 401 is the expected
  // "wrong credentials" response and must NOT trigger a redirect.
  if (res.status === 401 && token) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      // Retry the original request once with the new access token
      const newToken = getToken();
      return fetch(url, {
        method: options.method || "GET",
        cache: "no-store",
        headers: {
          ...(!isFormData && { "Content-Type": "application/json" }),
          ...(newToken && { Authorization: `Bearer ${newToken}` }),
          ...(options.headers || {}),
        },
        body: options.body,
      });
    }

    // Refresh failed — clear session and redirect to login
    clearSession();
    window.location.href = "/login";
  }

  return res;
}
